import {
  $isTextNode,
  TextNode,
  type DOMConversion,
  type DOMConversionFn,
  type DOMConversionMap,
  type LexicalNode,
  type SerializedTextNode,
} from 'lexical';

/**
 * A drop-in TextNode replacement that preserves inline `color`,
 * `background-color`, `font-family`, and `font-size` when HTML is imported into
 * the editor.
 *
 * Lexical's built-in span/text import (applyTextFormatFromStyle) only carries
 * over bold/italic/underline/strikethrough/sub/superscript — it discards text
 * color, font family, and font size. Without this, styled text renders
 * correctly while you edit but vanishes whenever the saved HTML is re-imported
 * (reopening a template, or switching customers and back in the statement send
 * dialog). Export already works: TextNode.exportDOM writes the node's style
 * (incl. these properties) to cssText, so this only needs to patch the import
 * side.
 */
function patchStyleConversion(originalFn: DOMConversionFn): DOMConversionFn {
  return (element) => {
    const output = originalFn(element);
    if (output === null) return null;

    // The base map includes a `#text` entry whose node is a DOM Text node with
    // no `style` — only element nodes carry inline styling.
    if (!(element instanceof HTMLElement)) return output;
    const { color, backgroundColor, fontFamily, fontSize } = element.style;
    if (!color && !backgroundColor && !fontFamily && !fontSize) return output;

    const extraStyle = [
      color ? `color: ${color}` : null,
      backgroundColor ? `background-color: ${backgroundColor}` : null,
      fontFamily ? `font-family: ${fontFamily}` : null,
      fontSize ? `font-size: ${fontSize}` : null,
    ]
      .filter((part): part is string => part !== null)
      .join('; ');

    const originalForChild = output.forChild;
    return {
      ...output,
      forChild: (
        lexicalNode: LexicalNode,
        parent: LexicalNode | null | undefined,
      ) => {
        const result = originalForChild
          ? originalForChild(lexicalNode, parent)
          : lexicalNode;
        if ($isTextNode(result)) {
          const existing = result.getStyle();
          result.setStyle(existing ? `${existing}; ${extraStyle}` : extraStyle);
        }
        return result;
      },
    };
  };
}

export class ExtendedTextNode extends TextNode {
  static getType(): string {
    return 'extended-text';
  }

  static clone(node: ExtendedTextNode): ExtendedTextNode {
    return new ExtendedTextNode(node.__text, node.__key);
  }

  // Wrap every built-in TextNode importer so inline color survives import.
  // Bumped one priority above the defaults (all priority 0) so these win.
  static importDOM(): DOMConversionMap | null {
    const base = TextNode.importDOM?.() ?? null;
    if (base === null) return null;
    const patched: DOMConversionMap = {};
    for (const [nodeName, propFn] of Object.entries(base)) {
      patched[nodeName] = (node) => {
        const conversion: DOMConversion | null = propFn(node);
        if (conversion === null) return null;
        return {
          conversion: patchStyleConversion(conversion.conversion),
          priority: 1,
        };
      };
    }
    return patched;
  }

  static importJSON(serializedNode: SerializedTextNode): ExtendedTextNode {
    const node = new ExtendedTextNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedTextNode {
    return { ...super.exportJSON(), type: 'extended-text', version: 1 };
  }
}
